import { z } from 'zod';
import { aHeureParis, ajouterJoursOuvres, bornesJourParis, bornesSemaineParis } from './calendrier.js';
import type { ResultatInteraction, TypeProchaineEtape } from './domain.js';
import {
  paginationSchema,
  type DirigeantDTO,
  type InteractionDTO,
  type LeadResumeDTO,
  type ReponsePaginee,
  type StatutCommercialDTO,
  type UtilisateurResumeDTO,
} from './dto.js';
import type { StatutScript } from './ia.js';

/**
 * Prospection (conception 7) : horizons des relances, suggestions apres un
 * appel, requetes et DTO des routes `/prospection/*`. Partage par l'API (qui
 * filtre) et l'interface (qui affiche et propose) : une seule regle.
 */

/* -------------------------------------------------------------------------- */
/*                                   Horizons                                  */
/* -------------------------------------------------------------------------- */

export const HORIZONS_PROSPECTION = ['retard', 'jour', 'semaine', 'plus_tard'] as const;
export type HorizonProspection = (typeof HORIZONS_PROSPECTION)[number];

/** Onglets de l'ecran Relances (conception 7.3). */
export const LIBELLES_HORIZON_PROSPECTION: Record<HorizonProspection, string> = {
  retard: 'En retard',
  jour: "Aujourd'hui",
  semaine: 'Cette semaine',
  plus_tard: 'Plus tard',
};

/** Fenetre `[debut, fin[` d'un horizon ; `null` : non bornee de ce cote. */
export interface FenetreHorizon {
  debut: Date | null;
  fin: Date | null;
}

/**
 * Fenetre d'un horizon, heure de Paris. Le retard se juge au jour : une
 * echeance de ce matin est « aujourd'hui ». « Cette semaine » est le reste de
 * la semaine lundi-dimanche apres aujourd'hui : vide le dimanche.
 */
export function intervalleHorizon(horizon: HorizonProspection, maintenant: Date = new Date()): FenetreHorizon {
  const jour = bornesJourParis(maintenant);
  const semaine = bornesSemaineParis(maintenant);
  switch (horizon) {
    case 'retard':
      return { debut: null, fin: jour.debut };
    case 'jour':
      return { debut: jour.debut, fin: jour.fin };
    case 'semaine':
      return { debut: jour.fin, fin: semaine.fin };
    case 'plus_tard':
      return { debut: semaine.fin, fin: null };
  }
}

/** Horizon ou tombe une echeance. */
export function horizonDe(date: Date, maintenant: Date = new Date()): HorizonProspection {
  const jour = bornesJourParis(maintenant);
  if (date < jour.debut) return 'retard';
  if (date < jour.fin) return 'jour';
  return date < bornesSemaineParis(maintenant).fin ? 'semaine' : 'plus_tard';
}

/* -------------------------------------------------------------------------- */
/*                       Suggestions apres un appel (7.1)                      */
/* -------------------------------------------------------------------------- */

export interface DelaiProchaineEtape {
  type: TypeProchaineEtape;
  /** Jours ouvres apres l'appel (samedi et dimanche sautes, jours feries ignores). */
  joursOuvres: number;
  /** Heure de Paris. */
  heure: number;
}

/** Table de la conception 7.1 ; `null` : aucune suite proposee. */
export const DELAIS_PROCHAINE_ETAPE: Readonly<Record<ResultatInteraction, DelaiProchaineEtape | null>> = {
  JOIGNABLE: { type: 'APPEL', joursOuvres: 5, heure: 10 },
  NON_JOIGNABLE: { type: 'APPEL', joursOuvres: 2, heure: 10 },
  REPONDEUR: { type: 'APPEL', joursOuvres: 2, heure: 10 },
  RAPPEL_DEMANDE: { type: 'APPEL', joursOuvres: 1, heure: 10 },
  INTERESSE: { type: 'APPEL', joursOuvres: 3, heure: 10 },
  PAS_INTERESSE: null,
  RDV_FIXE: { type: 'RDV', joursOuvres: 2, heure: 14 },
  AUTRE: null,
};

/** Prochaine etape proposee, a presenter pre-remplie et modifiable (compatible avec `ProchaineEtape`). */
export interface ProchaineEtapeSuggeree {
  type: TypeProchaineEtape;
  /** Echeance, ISO. */
  date: string;
}

export function suggererProchaineEtape(
  resultat: ResultatInteraction,
  maintenant: Date = new Date(),
): ProchaineEtapeSuggeree | null {
  const delai = DELAIS_PROCHAINE_ETAPE[resultat];
  if (!delai) return null;
  const jour = ajouterJoursOuvres(maintenant, delai.joursOuvres);
  return { type: delai.type, date: aHeureParis(jour, delai.heure).toISOString() };
}

/** Code de l'etape « RDV pris » du pipeline livre (`STATUTS_COMMERCIAUX_DEFAUT`). */
export const CODE_ETAPE_RDV_PRIS = 'RDV_PRIS';

/** Ce que la suggestion d'etape lit d'une etape du pipeline. */
export type EtapePipeline = Pick<StatutCommercialDTO, 'code' | 'ordre' | 'estInitial' | 'estFinal' | 'estGagne'>;

/**
 * Etape proposee apres un appel (conception 7.1), pre-cochee par l'interface :
 * RDV fixe -> `RDV_PRIS` ; pas interesse -> premiere etape finale non gagnee ;
 * sinon, depuis l'etape initiale, l'etape suivante non finale. Garde-fous :
 * rien pour un lead deja en etape finale (le cycle est clos, le rouvrir se
 * decide a la main) et jamais de recul dans le pipeline. `statutActuel` nul :
 * lead sans ligne de suivi, donc a l'etape initiale.
 */
export function suggererEtape<T extends EtapePipeline>(
  resultat: ResultatInteraction,
  statutActuel: string | null,
  statuts: readonly T[],
): T | null {
  const ordonnees = [...statuts].sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code));
  const actuelle =
    statutActuel === null
      ? (ordonnees.find((statut) => statut.estInitial) ?? ordonnees[0])
      : ordonnees.find((statut) => statut.code === statutActuel);
  if (actuelle === undefined || actuelle.estFinal) return null;
  const apres = (statut: T): boolean => statut.ordre > actuelle.ordre;

  if (resultat === 'PAS_INTERESSE') {
    return ordonnees.find((statut) => statut.estFinal && !statut.estGagne && apres(statut)) ?? null;
  }
  if (resultat === 'RDV_FIXE') {
    const rdvPris = ordonnees.find((statut) => statut.code === CODE_ETAPE_RDV_PRIS);
    if (rdvPris && apres(rdvPris)) return rdvPris;
  }
  if (statutActuel !== null && !actuelle.estInitial) return null;
  return ordonnees.find((statut) => apres(statut) && !statut.estFinal && !statut.estInitial) ?? null;
}

/* -------------------------------------------------------------------------- */
/*                                   Requetes                                  */
/* -------------------------------------------------------------------------- */

/** `GET /prospection/relances`. */
export const requeteRelancesProspectionSchema = paginationSchema.extend({
  horizon: z.enum(HORIZONS_PROSPECTION).default('retard'),
  /** Commercial dont on lit les relances ; un autre que soi exige `stats.equipe`. */
  proprietaireId: z.string().uuid().optional(),
});
export type RequeteRelancesProspection = z.infer<typeof requeteRelancesProspectionSchema>;

export const NOUVEAUX_FILE_DEFAUT = 20;
export const NOUVEAUX_FILE_MAX = 100;

/** `GET /prospection/file-appels`. */
export const requeteFileAppelsSchema = z.object({
  /** Leads jamais contactes ajoutes apres les relances d'appel. */
  nouveaux: z.coerce.number().int().min(0).max(NOUVEAUX_FILE_MAX).default(NOUVEAUX_FILE_DEFAUT),
});
export type RequeteFileAppels = z.infer<typeof requeteFileAppelsSchema>;

/** Relances d'appel chargees au plus dans une file ; `totalRelances` donne leur nombre reel. */
export const MAX_RELANCES_FILE = 200;
/** Un script pret plus recent n'est pas regenere par le prechargement (conception 7.4). */
export const JOURS_VALIDITE_SCRIPT = 30;
/** Etapes du jour et en retard listees par l'ecran Aujourd'hui. */
export const LIMITE_ETAPES_AUJOURDHUI = 50;
/** Derniers leads attribues montres par l'ecran Aujourd'hui. */
export const NB_DERNIERS_ATTRIBUES = 5;

/* -------------------------------------------------------------------------- */
/*                                      DTO                                    */
/* -------------------------------------------------------------------------- */

/** Prochaine etape d'un lead, telle que l'affichent les vues de prospection. */
export interface ProchaineEtapeDTO {
  /** `null` : prochaine etape saisie avant leur typage (la file d'appels la presume un appel). */
  type: TypeProchaineEtape | null;
  /** Echeance, ISO. */
  date: string;
  note: string | null;
  /** Ou tombe l'echeance, heure de Paris. */
  horizon: HorizonProspection;
}

/** Badge de navigation (conception 7.5) : `enRetard + aujourdhui`. */
export interface CompteursProspectionDTO {
  enRetard: number;
  aujourdhui: number;
}

export interface RelanceProspectionDTO {
  lead: LeadResumeDTO;
  prochaineEtape: ProchaineEtapeDTO;
}

export interface PageRelancesDTO extends ReponsePaginee<RelanceProspectionDTO> {
  /** Relances du meme proprietaire par horizon, pour les onglets. */
  parHorizon: Record<HorizonProspection, number>;
}

/** RELANCE : appel du ou en retard ; NOUVEAU : lead jamais contacte. */
export const MOTIFS_FILE_APPELS = ['RELANCE', 'NOUVEAU'] as const;
export type MotifFileAppels = (typeof MOTIFS_FILE_APPELS)[number];

/** Scripts d'appel d'un lead de la file (prechargement, conception 7.4). */
export interface ScriptFileAppelsDTO {
  /** Dernier script demande, quel que soit son statut. */
  dernierId: string;
  dernierStatut: StatutScript;
  /** Script PRET de moins de `JOURS_VALIDITE_SCRIPT` jours ; `null` : en demander un. */
  pretId: string | null;
}

export interface ElementFileAppelsDTO {
  motif: MotifFileAppels;
  lead: LeadResumeDTO;
  /** Numero a composer : saisi a la main, sinon Google. */
  telephone: string;
  /** Dirigeant principal (nom et fonction). */
  contact: DirigeantDTO | null;
  /**
   * Dernier contact (appel, e-mail, RDV, demo, ou interaction portant un
   * resultat, tentative sans reponse comprise) ; une note sans resultat n'en
   * est pas un.
   */
  dernierEchange: InteractionDTO | null;
  prochaineEtape: ProchaineEtapeDTO | null;
  script: ScriptFileAppelsDTO | null;
}

export interface FileAppelsDTO {
  /** Relances d'appel (retards d'abord) puis nouveaux leads. */
  elements: ElementFileAppelsDTO[];
  /** Relances d'appel eligibles, y compris au-dela de `MAX_RELANCES_FILE`. */
  totalRelances: number;
  /** Relances d'appel et nouveaux leads de l'appelant ecartes faute de telephone. */
  sansTelephone: number;
}

/** Bandeau « Demarrer ma session (n appels) ». */
export interface ResumeFileAppelsDTO {
  taille: number;
  /** Leads de la file qui ont deja un script pret de moins de 30 jours. */
  avecScriptPret: number;
  sansTelephone: number;
}

/** Ligne « equipe » de l'ecran Aujourd'hui. */
export interface RetardsMembreDTO {
  membre: UtilisateurResumeDTO;
  enRetard: number;
}

export interface CompteursAujourdhuiDTO extends CompteursProspectionDTO {
  /** Rendez-vous et demos de la semaine lundi-dimanche, jours passes compris. */
  rdvSemaine: number;
  /** Appels consignes aujourd'hui par l'appelant. */
  appelsAujourdhui: number;
}

export interface AujourdhuiDTO {
  /** Jour civil de Paris, `AAAA-MM-JJ`. */
  jour: string;
  compteurs: CompteursAujourdhuiDTO;
  /** Relances du jour et en retard, retards d'abord, `LIMITE_ETAPES_AUJOURDHUI` au plus. */
  etapes: RelanceProspectionDTO[];
  /** Leads de l'appelant jamais contactes, les plus recemment attribues (ou modifies) d'abord. */
  derniersAttribues: LeadResumeDTO[];
  /** `null` sans `leads.ecrire`. */
  fileAppels: ResumeFileAppelsDTO | null;
  /** Relances en retard par membre ; `null` sans `stats.equipe`. */
  equipe: RetardsMembreDTO[] | null;
}
